/**
 * ObjectiveModal Component
 *
 * Add/Edit modal for trading objectives.
 * Supports dual mode (add vs edit), form validation, and tag management.
 */

// 1. External dependencies
import React, { useState, useEffect, useCallback, useMemo } from 'react';

// 2. Internal absolute imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  TextArea,
  Select,
  SelectItem,
  Chip,
  Alert,
  Divider,
  Autocomplete,
  AutocompleteItem,
  useDisclosure
} from '@enfomentis/ui';
import { Plus, Settings } from 'lucide-react';

// 3. Services & Types
import { tradingObjectiveService } from '@/services/TradingObjectiveService';
import { tradingObjectiveSettingsService } from '@/services/TradingObjectiveSettingsService';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import type { TradingObjective } from '@/types/schema/TradingObjective';
import type { ObjectiveType } from '@/types/schema/TradingObjectiveSettings';

// 4. Local imports
import { ObjectiveTypesModal } from './ObjectiveTypesModal';

/**
 * Form data for objective modal
 */
interface ObjectiveFormData {
  name: string;
  description: string;
  type: string; // ObjectiveType.id
  tags: string[];
}

/**
 * Form validation errors
 */
interface FormErrors {
  name?: string;
  type?: string;
}

/**
 * Props for ObjectiveModal
 */
export interface ObjectiveModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal closes */
  onClose: () => void;
  /** Objective to edit (undefined for add mode) */
  objective?: TradingObjective;
  /**
   * Callback after successful save.
   * In add mode, passes the newly created TradingObjective (for inline creation flows).
   * In edit mode, passes undefined.
   */
  onSave: (newObjective?: TradingObjective) => void;
  /** Optional callback for errors */
  onError?: (error: Error) => void;
  /** Existing tags from all objectives for autocomplete suggestions */
  existingTags?: string[];
}

/**
 * Special key for "Manage Types..." option
 */
const MANAGE_TYPES_KEY = '__manage_types__';

/**
 * Normalize tag for comparison (not storage)
 * - lowercase
 * - trim whitespace
 * - collapse separators (spaces, hyphens, underscores) to single space
 */
const normalizeTagForComparison = (tag: string): string => {
  return tag
    .toLowerCase()
    .trim()
    .replace(/[\s\-_]+/g, ' ');
};

/** Maximum number of suggestions to show */
const MAX_SUGGESTIONS = 8;

/**
 * Initial empty form state
 */
const initialFormData: ObjectiveFormData = {
  name: '',
  description: '',
  type: '',
  tags: []
};

/**
 * ObjectiveModal Component
 */
export const ObjectiveModal: React.FC<ObjectiveModalProps> = ({
  isOpen,
  onClose,
  objective,
  onSave,
  onError,
  existingTags = []
}) => {
  // Form state
  const [formData, setFormData] = useState<ObjectiveFormData>(initialFormData);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Objective types state
  const [objectiveTypes, setObjectiveTypes] = useState<ObjectiveType[]>([]);
  const [typesLoading, setTypesLoading] = useState(true);

  // Tag input state
  const [tagInput, setTagInput] = useState('');

  // Pre-dedupe existingTags using normalized comparison
  const dedupedExistingTags = useMemo(() => {
    const seen = new Map<string, string>(); // normalized -> original
    for (const tag of existingTags) {
      const normalized = normalizeTagForComparison(tag);
      if (!seen.has(normalized)) {
        seen.set(normalized, tag);
      }
    }
    return Array.from(seen.values());
  }, [existingTags]);

  // Filter suggestions based on input
  const filteredSuggestions = useMemo(() => {
    const trimmedInput = tagInput.trim();
    if (!trimmedInput) return [];

    const normalizedInput = normalizeTagForComparison(trimmedInput);
    const normalizedFormTags = new Set(formData.tags.map(normalizeTagForComparison));

    return dedupedExistingTags
      .filter((tag) => {
        const normalizedTag = normalizeTagForComparison(tag);
        // Must contain the input and not already be in form tags
        return (
          normalizedTag.includes(normalizedInput) &&
          !normalizedFormTags.has(normalizedTag)
        );
      })
      .slice(0, MAX_SUGGESTIONS);
  }, [tagInput, dedupedExistingTags, formData.tags]);

  // ObjectiveTypesModal state
  const typesModalDisclosure = useDisclosure();

  // Determine mode
  const isEditMode = !!objective;

  /**
   * Load objective types from settings
   */
  const loadObjectiveTypes = useCallback(async () => {
    try {
      setTypesLoading(true);
      const settings = await tradingObjectiveSettingsService.getOrCreateDefaultSettings();
      setObjectiveTypes(settings.objectiveTypes);
    } catch (error) {
      console.error('Failed to load objective types:', error);
    } finally {
      setTypesLoading(false);
    }
  }, []);

  // Load types on mount
  useEffect(() => {
    loadObjectiveTypes();
  }, [loadObjectiveTypes]);

  // Populate form when objective prop changes (edit mode)
  useEffect(() => {
    if (objective) {
      setFormData({
        name: objective.name,
        description: objective.description || '',
        type: objective.type,
        tags: [...objective.tags]
      });
    }
  }, [objective]);

  // Reset form when modal opens in add mode
  useEffect(() => {
    if (isOpen && !objective) {
      setFormData(initialFormData);
      setErrors({});
      setSubmitError(null);
      setTagInput('');
    }
  }, [isOpen, objective]);

  /**
   * Validate form data
   */
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.type && objectiveTypes.length > 0) {
      newErrors.type = 'Type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form field changes
   */
  const handleFieldChange = useCallback((field: keyof ObjectiveFormData, value: string | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when field is edited
    setErrors(prev => {
      if (prev[field as keyof FormErrors]) {
        return { ...prev, [field]: undefined };
      }
      return prev;
    });
  }, []);

  /**
   * Handle type selection
   */
  const handleTypeChange = (keys: 'all' | Set<React.Key>) => {
    if (keys === 'all') return;
    const selectedKey = Array.from(keys)[0]?.toString();

    if (selectedKey === MANAGE_TYPES_KEY) {
      // Open types modal without changing selection
      typesModalDisclosure.onOpen();
      return;
    }

    if (selectedKey) {
      handleFieldChange('type', selectedKey);
    }
  };

  /**
   * Add a tag from input (uses normalized comparison for deduplication)
   */
  const addTag = useCallback((value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;

    // Use normalized comparison for deduplication
    const normalizedNew = normalizeTagForComparison(trimmed);
    const isDuplicate = formData.tags.some(
      (existingTag) => normalizeTagForComparison(existingTag) === normalizedNew
    );

    if (!isDuplicate) {
      handleFieldChange('tags', [...formData.tags, trimmed]);
    }
    setTagInput('');
  }, [formData.tags, handleFieldChange]);

  /**
   * Handle selection from Autocomplete suggestions
   * Called when user selects a suggestion via click or Enter
   */
  const handleSelectionChange = useCallback((key: React.Key | null) => {
    // Ignore null selections (happens when clearing or closing)
    if (key === null) return;

    // Key is the suggestion text - add it as a tag
    addTag(key.toString());
  }, [addTag]);

  /**
   * Handle tag input key press for custom tags
   * - Comma: always add typed text
   * - Enter: add typed text only when no suggestions (otherwise Autocomplete handles via onSelectionChange)
   */
  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === ',') {
      e.preventDefault();
      addTag(tagInput);
    } else if (e.key === 'Enter' && filteredSuggestions.length === 0 && tagInput.trim()) {
      // Only handle Enter when no suggestions - Autocomplete handles it when suggestions exist
      e.preventDefault();
      addTag(tagInput);
    }
  };

  /**
   * Remove a tag
   */
  const removeTag = (tagToRemove: string) => {
    handleFieldChange('tags', formData.tags.filter(tag => tag !== tagToRemove));
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async () => {
    setSubmitError(null);

    // Validate
    if (!validateForm()) {
      return;
    }

    // Check if types are configured
    if (objectiveTypes.length === 0) {
      setSubmitError('Please configure at least one objective type before saving.');
      return;
    }

    try {
      setIsSubmitting(true);

      // Deduplicate tags using Set
      const uniqueTags = [...new Set(formData.tags)];
      const trimmedDescription = formData.description.trim();

      if (isEditMode && objective) {
        // Update existing objective - preserve status and usageHistory
        await tradingObjectiveService.update(objective.id, {
          name: formData.name.trim(),
          description: trimmedDescription, // Always include to allow clearing
          type: formData.type,
          tags: uniqueTags,
          // Preserve existing fields
          status: objective.status,
          usageHistory: objective.usageHistory
        });
        // Notify for cross-tab sync (BaseService.update doesn't call notifyChange)
        syncCoordinatorService.notifyChange('trading_objectives', objective.id);
        // Edit mode: no new objective to pass
        onSave(undefined);
      } else {
        // Create new objective - set defaults
        const newObjective = await tradingObjectiveService.add({
          name: formData.name.trim(),
          type: formData.type,
          tags: uniqueTags,
          status: 'active',
          usageHistory: [],
          // Only include description if not empty
          ...(trimmedDescription && { description: trimmedDescription })
        });
        // Notify for cross-tab sync (BaseService.add doesn't call notifyChange)
        syncCoordinatorService.notifyChange('trading_objectives', newObjective.id);
        // Add mode: pass new objective for inline creation flows
        onSave(newObjective);
      }

      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save objective';
      setSubmitError(errorMessage);
      onError?.(error instanceof Error ? error : new Error(errorMessage));
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle ObjectiveTypesModal close - refresh types
   */
  const handleTypesModalClose = () => {
    typesModalDisclosure.onClose();
    loadObjectiveTypes();
  };

  // Check if save should be disabled
  const isSaveDisabled = isSubmitting || typesLoading || objectiveTypes.length === 0;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        placement="top-center"
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader>
            {isEditMode ? 'Edit Objective' : 'Add Objective'}
          </ModalHeader>

          <ModalBody className="gap-4">
            {/* Name */}
            <Input
              label="Name"
              placeholder="Enter objective name"
              value={formData.name}
              onValueChange={(value) => handleFieldChange('name', value)}
              isRequired
              isInvalid={!!errors.name}
              errorMessage={errors.name}
              autoFocus
              className="[&_input]:text-base"
            />

            {/* Description */}
            <TextArea
              label="Description"
              placeholder="Optional description or details"
              value={formData.description}
              onValueChange={(value) => handleFieldChange('description', value)}
              minRows={2}
              maxRows={4}
              className="[&_textarea]:text-base"
            />

            {/* Type */}
            <Select
              label="Type"
              placeholder={typesLoading ? 'Loading types...' : 'Select type'}
              selectedKeys={formData.type ? new Set([formData.type]) : new Set()}
              onSelectionChange={handleTypeChange}
              isRequired
              isInvalid={!!errors.type}
              errorMessage={errors.type}
              isDisabled={typesLoading}
              description={objectiveTypes.length === 0 && !typesLoading ? 'Please configure at least one type' : undefined}
              className="[&_button]:text-base"
            >
              {objectiveTypes.length === 0 && !typesLoading
                ? [
                    <SelectItem key="__no_types__" isDisabled>
                      No types configured
                    </SelectItem>,
                    <SelectItem
                      key={MANAGE_TYPES_KEY}
                      startContent={<Settings size={16} />}
                      className="text-primary"
                    >
                      Manage Types...
                    </SelectItem>
                  ]
                : [
                    ...objectiveTypes.map((type) => (
                      <SelectItem key={type.id}>
                        {type.name}
                      </SelectItem>
                    )),
                    <SelectItem
                      key={MANAGE_TYPES_KEY}
                      startContent={<Settings size={16} />}
                      className="text-primary"
                    >
                      Manage Types...
                    </SelectItem>
                  ]
              }
            </Select>

            {/* Tags with Autocomplete */}
            <div className="space-y-2">
              <Autocomplete
                label="Tags"
                placeholder="Type to search or add new tags"
                description={existingTags.length > 0 ? "Select from suggestions or type custom tags" : undefined}
                allowsCustomValue={true}
                inputValue={tagInput}
                onInputChange={setTagInput}
                onSelectionChange={handleSelectionChange}
                onKeyDown={handleTagKeyDown}
                menuTrigger="input"
                className="[&_input]:text-base"
                endContent={
                  tagInput.trim() && (
                    <Button
                      size="sm"
                      variant="light"
                      isIconOnly
                      onPress={() => addTag(tagInput)}
                    >
                      <Plus size={16} />
                    </Button>
                  )
                }
              >
                {filteredSuggestions.map((suggestion) => (
                  <AutocompleteItem key={suggestion}>
                    {suggestion}
                  </AutocompleteItem>
                ))}
              </Autocomplete>
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {formData.tags.map((tag, index) => (
                    <Chip
                      key={index}
                      size="sm"
                      variant="flat"
                      onClose={() => removeTag(tag)}
                    >
                      {tag}
                    </Chip>
                  ))}
                </div>
              )}
            </div>

            {/* Error Alert */}
            {submitError && (
              <>
                <Divider />
                <Alert color="danger" variant="flat">
                  {submitError}
                </Alert>
              </>
            )}
          </ModalBody>

          <ModalFooter>
            <Button variant="flat" onPress={onClose} isDisabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              color="primary"
              onPress={handleSubmit}
              isLoading={isSubmitting}
              isDisabled={isSaveDisabled}
            >
              {isEditMode ? 'Save Changes' : 'Add Objective'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ObjectiveTypesModal */}
      <ObjectiveTypesModal
        isOpen={typesModalDisclosure.isOpen}
        onClose={handleTypesModalClose}
      />
    </>
  );
};

export default ObjectiveModal;
